import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { Navigate, Link } from 'react-router-dom';
import { useLoginMutation } from 'services/kewloxApi';
import { selectIsAuthenticated } from 'app/containers/AuthProvider/selectors';
import { setToken } from 'app/containers/AuthProvider/slice';
import { Button } from 'components/ui/button';
import { Mail, Lock } from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from 'components/ui/card';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import logo from 'assets/images/logo.png';

export function LoginPage() {
  const [displayHelp, showHelp] = useState(false);

  const isAuthenticated = useSelector(selectIsAuthenticated);
  const [login, { data, error, isLoading }] = useLoginMutation();
  const dispatch = useDispatch();
  const [errorsList, setErrors] = useState<string | null>(null);

  const schema = Yup.object({
    username: Yup.string().required('Champ requis'),
    password: Yup.string().required('Champ requis'),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  const onSubmit = async values => {
    setErrors(null);
    try {
      await login(values).unwrap();
    } catch (err) {
      setErrors(
        'Impossible de se connecter, vérifier votre login/mot de passe',
      );
    }
  };

  useEffect(() => {
    if (data) {
      dispatch(setToken(data.token));
    }
  }, [data]);

  return (
    <>
      <Helmet>
        <title>Connexion</title>
        <meta name="description" content="Connexion" />
      </Helmet>

      {isAuthenticated && <Navigate to="/" />}

      <div className="flex min-h-screen w-full items-center justify-center bg-gray-100 p-4 dark:bg-gray-950">
        <Card className="w-full max-w-md p-8 shadow-xl transition-shadow duration-300 hover:shadow-2xl">
          <CardHeader className="text-center">
            <div className="flex items-center justify-center mb-6">
              <img src={logo} alt="logo" className="mx-auto h-12 w-auto" />
            </div>
            <CardTitle className="mt-6 text-3xl font-extrabold tracking-tight text-gray-900 dark:text-gray-50">
              Expedinet
            </CardTitle>
            <CardDescription className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              Veuillez vous connecter pour continuer
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="username">Username</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 transform text-gray-400" />
                  <Input
                    id="username"
                    placeholder="your@email.com"
                    {...register('username')}
                    className="pl-10"
                  />
                </div>
                {errors.username && (
                  <p className="text-sm text-red-600">
                    {errors.username.message}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">Mot de passe</Label>
                  <Link
                    to="/password-reset"
                    className="text-sm font-medium text-primary underline-offset-4 hover:underline"
                  >
                    Mot de passe oublié?
                  </Link>
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 transform text-gray-400" />
                  <Input
                    id="password"
                    type="password"
                    {...register('password')}
                    className="pl-10"
                  />
                </div>
                {errors.password && (
                  <p className="text-sm text-red-600">
                    {errors.password.message}
                  </p>
                )}
              </div>
              {errorsList && (
                <p className="text-sm text-red-600">{errorsList}</p>
              )}
              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? 'Connexion...' : 'Se connecter'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </>
  );
}
